import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import ArogyaIcon from '../images/arogyaIcon.png';
import { Grid, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'reduxStore/hooks';
import { erpnextLogin, updateErpnextLoginClearMsg } from 'reduxStore/login/loginSlice';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  [theme.breakpoints.up('sm')]: {
    width: '450px',
  },
}));

export default function SignInCard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, successMsg, error } = useAppSelector((state: any) => state.loginDetails);

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  React.useEffect(() => {
    if (successMsg) {
      // Navigate to home on successful login
      navigate('/home');
      window.location.reload();
      dispatch(updateErpnextLoginClearMsg());
    }
  }, [successMsg, navigate, dispatch]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Reset errors
    setEmailError(false);
    setEmailErrorMessage('');
    setPasswordError(false);
    setPasswordErrorMessage('');
    dispatch(updateErpnextLoginClearMsg());

    const data = new FormData(event.currentTarget);
    const email = data.get('email') as string;
    const password = data.get('password') as string;

    // Validate inputs
    let isValid = true;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    }

    if (!password || password.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    // Dispatch login action
    dispatch(erpnextLogin({ username: email, password }));
  };

  return (
    <Card variant="outlined">
      {/* <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
        <img src={ArogyaIcon} alt="Arogya Logo" width={220} />
      </Box> */}
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Grid item>
          <img src={ArogyaIcon} alt="Arogya Logo" width={70} />
        </Grid>
        <Grid item>
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            Sign in
          </Typography>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
      >
        <FormControl>
          <FormLabel htmlFor="email">Email</FormLabel>
          <TextField
            error={emailError}
            helperText={emailErrorMessage}
            id="email"
            type="email"
            name="email"
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            required
            fullWidth
            variant="outlined"
            color={emailError ? 'error' : 'primary'}
            disabled={loading}
          />
        </FormControl>
        <FormControl error={passwordError}>
          <FormLabel htmlFor="password">Password</FormLabel>
          <OutlinedInput
            id="password"
            type={showPassword ? 'text' : 'password'}
            error={passwordError}
            name="password"
            placeholder="••••••"
            disabled={loading}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? 'hide the password' : 'display the password'
                  }
                  onClick={handleClickShowPassword}
                  edge="end"
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Password"
          />
          {passwordErrorMessage && (
            <FormHelperText>{passwordErrorMessage}</FormHelperText>
          )}
        </FormControl>
        <FormControlLabel
          control={<Checkbox value="remember" color="primary" disabled={loading} />}
          label="Remember me"
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </Button>
      </Box>
    </Card>
  );
}